import { createFileRoute, useNavigate } from '@tanstack/solid-router';
import { For, Show, createMemo, createSignal, onMount } from 'solid-js';

import Header from '~/components/layout/Header';
import { COUNTRY_CODE, OFFER_TYPE, type Quote } from '~/lib/graphql/types';
import { trackEvent } from '~/lib/helpers/analytics';
import { decodeOffer } from '~/lib/helpers/offer-url';
import { posthog } from '~/lib/helpers/posthog';
import { countryCode, locale } from '~/lib/i18n';
import { useCalculator } from '~/lib/store';

export const Route = createFileRoute('/$locale/offer')({
    component: OfferPage
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtCurrency(
    value: string | number,
    currency: string,
    localeStr: string
): string {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return `${currency}0`;
    return `${currency}${num.toLocaleString(localeStr, { maximumFractionDigits: 0 })}`;
}

function fmtNumber(value: string | number, localeStr: string): string {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    return num.toLocaleString(localeStr, { maximumFractionDigits: 2 });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type OfferTab = 'RTO' | 'PPA' | 'UPFRONT';

function OfferPage() {
    const [state] = useCalculator();
    const navigate = useNavigate();
    const [tab, setTab] = createSignal<OfferTab>('RTO');
    const [offer, setOffer] = createSignal<Quote | null>(null);
    const [copied, setCopied] = createSignal(false);
    const rootContext = Route.useRouteContext() as unknown as () => {
        config: { whatsappLink: string };
    };

    onMount(() => {
        posthog.capture('$pageview');

        const hash = window.location.hash.slice(1);
        if (!hash) {
            navigate({ to: '/$locale', search: (prev) => prev });
            return;
        }
        const decoded = decodeOffer(hash);
        if (!decoded) {
            navigate({ to: '/$locale', search: (prev) => prev });
            return;
        }
        setOffer(decoded);

        trackEvent('Step: offer', {
            quote: state.quote,
            set: state.set,
            direction: state.direction,
            utm: state.utm
        });

        const types = decoded.applicableOfferTypes ?? [];
        if (
            types.includes(OFFER_TYPE.TEN_YEAR_RTO) ||
            types.includes(OFFER_TYPE.FIVE_YEAR_RTO)
        ) {
            setTab('RTO');
        } else if (types.includes(OFFER_TYPE.PPA)) {
            setTab('PPA');
        } else {
            setTab('UPFRONT');
        }
    });

    const cur = () => locale().currency;
    const loc = () => locale().localeString;
    const o = () => offer()!;

    const hasRto = createMemo(() => {
        const t = offer()?.applicableOfferTypes ?? [];
        return (
            t.includes(OFFER_TYPE.TEN_YEAR_RTO) ||
            t.includes(OFFER_TYPE.FIVE_YEAR_RTO)
        );
    });
    const hasPpa = createMemo(() =>
        (offer()?.applicableOfferTypes ?? []).includes(OFFER_TYPE.PPA)
    );
    const has10yr = createMemo(() =>
        (offer()?.applicableOfferTypes ?? []).includes(OFFER_TYPE.TEN_YEAR_RTO)
    );

    const rtoCards = createMemo(() => {
        if (!hasRto()) return [];
        const opts = offer()?.rtoOptions ?? [];
        const isSG = countryCode() === COUNTRY_CODE.SG;

        const cards = [
            has10yr() && {
                term: 10,
                upfront: opts[0]?.cost ?? 0,
                monthly: opts[0]?.payment ?? 0,
                recommended: isSG
            },
            {
                term: 5,
                upfront: opts[1]?.cost ?? 0,
                monthly: opts[1]?.payment ?? 0,
                recommended: !isSG || !has10yr()
            }
        ].filter(Boolean) as {
            term: number;
            upfront: number;
            monthly: number;
            recommended: boolean;
        }[];

        return isSG ? cards : cards.reverse();
    });

    function handleShare() {
        navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }

    return (
        <div class="min-h-screen bg-white">
            <Header />

            <Show
                when={offer()}
                fallback={
                    <div class="p-12 text-center text-gray-400">
                        Loading offer...
                    </div>
                }>
                {() => (
                    <div class="w-[288px] xs:w-[358px] sm:w-[560px] md:w-[660px] mt-8 pb-16 mx-auto">
                        {/* Title */}
                        <h1 class="hidden sm:block text-[32px] font-bold text-brand-black text-center">
                            Your personalized Solar offer
                        </h1>

                        {/* ── Tabs ── */}
                        <div class="flex items-center justify-between gap-2 sm:gap-10 text-sm sm:text-base sm:mt-10">
                            <Show when={hasRto()}>
                                <button
                                    class={`sm:px-2 pb-1 mx-auto flex items-center justify-center cursor-pointer border-b-2 transition-colors font-bold ${
                                        tab() === 'RTO'
                                            ? 'border-brand-blue text-brand-blue'
                                            : 'border-transparent text-[#99ABC9]'
                                    }`}
                                    onClick={() => setTab('RTO')}>
                                    Rent-to-Own
                                    <span class="inline-block px-1.5 py-0.5 ml-2 bg-brand-yellow rounded text-[10px] text-brand-black font-semibold">
                                        Most Popular
                                    </span>
                                </button>
                            </Show>
                            <Show when={hasPpa()}>
                                <button
                                    class={`sm:px-2 pb-1 mx-auto cursor-pointer border-b-2 transition-colors font-bold ${
                                        tab() === 'PPA'
                                            ? 'border-brand-blue text-brand-blue'
                                            : 'border-transparent text-[#99ABC9]'
                                    }`}
                                    onClick={() => setTab('PPA')}>
                                    Power Purchase Agreement
                                </button>
                            </Show>
                            <button
                                class={`sm:px-2 pb-1 mx-auto cursor-pointer border-b-2 transition-colors font-bold ${
                                    tab() === 'UPFRONT'
                                        ? 'border-brand-blue text-brand-blue'
                                        : 'border-transparent text-[#99ABC9]'
                                }`}
                                onClick={() => setTab('UPFRONT')}>
                                Upfront Purchase
                            </button>
                        </div>

                        {/* ── Offer Cards ── */}
                        <div class="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8 mt-8 sm:mt-10">
                            {/* RTO */}
                            <Show when={tab() === 'RTO'}>
                                <For each={rtoCards()}>
                                    {(card) => (
                                        <div
                                            class={`w-full sm:flex-1 rounded-2xl overflow-hidden text-brand-black ${
                                                card.recommended
                                                    ? 'shadow-lg ring-2 ring-brand-blue'
                                                    : 'border border-brand-clay'
                                            }`}>
                                            {/* Card header */}
                                            <div class="h-[76px] sm:h-[84px] bg-[#FFF8E1] flex items-center justify-center text-base font-bold">
                                                {card.term}-Year Rent-to-Own
                                            </div>
                                            {/* Card body */}
                                            <div class="bg-[#FFFDF7] px-6 py-6 text-center">
                                                <Show when={card.recommended}>
                                                    <span class="inline-block px-2 py-0.5 mb-3 bg-brand-yellow rounded text-xs font-semibold">
                                                        Recommended
                                                    </span>
                                                </Show>
                                                <p class="text-[32px] sm:text-5xl font-bold break-words">
                                                    {fmtCurrency(
                                                        card.upfront,
                                                        cur(),
                                                        loc()
                                                    )}
                                                </p>
                                                <p class="text-sm text-[#637381] mt-1">
                                                    Upfront cost
                                                </p>
                                                <div class="w-20 h-px bg-brand-clay mx-auto my-4" />
                                                <p class="text-xl sm:text-2xl font-bold">
                                                    {fmtCurrency(
                                                        card.monthly,
                                                        cur(),
                                                        loc()
                                                    )}
                                                    <span class="text-sm font-normal text-[#637381]">
                                                        /mo
                                                    </span>
                                                </p>
                                                <p class="text-sm text-[#637381] mt-1">
                                                    Fixed monthly fee
                                                </p>
                                            </div>
                                            {/* Card footer */}
                                            <div class="bg-white flex items-center justify-center h-[74px] sm:h-24">
                                                <span class="text-sm font-semibold text-brand-blue">
                                                    {o().installation.size} kWp
                                                    system
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </For>
                            </Show>

                            {/* PPA */}
                            <Show when={tab() === 'PPA'}>
                                <div class="w-full max-w-md rounded-2xl overflow-hidden border border-brand-clay text-brand-black">
                                    <div class="h-[76px] sm:h-[84px] bg-[#FFF8E1] flex items-center justify-center text-base font-bold">
                                        Power Purchase Agreement
                                    </div>
                                    <div class="bg-[#FFFDF7] px-6 py-6 text-center">
                                        <p class="text-[32px] sm:text-5xl font-bold">
                                            $0
                                        </p>
                                        <p class="text-sm text-[#637381] mt-1">
                                            Installation cost
                                        </p>
                                        <div class="w-20 h-px bg-brand-clay mx-auto my-4" />
                                        <p class="text-xl sm:text-2xl font-bold">
                                            $0.08 – $0.15
                                            <span class="text-sm font-normal text-[#637381]">
                                                /kWh
                                            </span>
                                        </p>
                                        <p class="text-sm text-[#637381] mt-1">
                                            Solar tariff
                                        </p>
                                    </div>
                                    <div class="bg-white flex items-center justify-center h-[74px] sm:h-24">
                                        <span class="text-sm font-semibold text-brand-blue">
                                            {o().installation.size} kWp system
                                        </span>
                                    </div>
                                </div>
                            </Show>

                            {/* UPFRONT */}
                            <Show when={tab() === 'UPFRONT'}>
                                <div class="w-full max-w-md rounded-2xl overflow-hidden border border-brand-clay text-brand-black">
                                    <div class="h-[76px] sm:h-[84px] bg-[#FFF8E1] flex items-center justify-center text-base font-bold">
                                        Upfront Purchase
                                    </div>
                                    <div class="bg-[#FFFDF7] px-6 py-6 text-center">
                                        <p class="text-[32px] sm:text-5xl font-bold break-words">
                                            {fmtCurrency(
                                                o().installation.cost.low,
                                                cur(),
                                                loc()
                                            )}{' '}
                                            –{' '}
                                            {fmtCurrency(
                                                o().installation.cost.high,
                                                cur(),
                                                loc()
                                            )}
                                        </p>
                                        <p class="text-sm text-[#637381] mt-1">
                                            Estimated cost
                                        </p>
                                        <div class="w-20 h-px bg-brand-clay mx-auto my-4" />
                                        <p class="text-xl sm:text-2xl font-bold">
                                            {o().installation.roi}
                                            <span class="text-sm font-normal text-[#637381]">
                                                {' '}
                                                years
                                            </span>
                                        </p>
                                        <p class="text-sm text-[#637381] mt-1">
                                            Return on investment
                                        </p>
                                    </div>
                                    <div class="bg-white flex items-center justify-center h-[74px] sm:h-24">
                                        <span class="text-sm font-semibold text-brand-blue">
                                            {o().installation.size} kWp system
                                        </span>
                                    </div>
                                </div>
                            </Show>
                        </div>

                        {/* ── Benefits ── */}
                        <div class="mt-10 sm:mt-12">
                            <ul class="space-y-3 max-w-sm mx-auto">
                                <Show when={tab() === 'RTO'}>
                                    <Benefit text="Zero upfront payment" />
                                    <Benefit text="Fixed monthly fee" />
                                    <Benefit text="Own your system after contract" />
                                    <Benefit text="Hassle-free, all out-of-pocket costs covered" />
                                    <Benefit text="Instant savings on electricity bills" />
                                </Show>
                                <Show when={tab() === 'PPA'}>
                                    <Benefit text="Zero upfront payment" />
                                    <Benefit text="Instant savings on electricity bills" />
                                    <Benefit text="Hassle-free, covers maintenance and insurance" />
                                </Show>
                                <Show when={tab() === 'UPFRONT'}>
                                    <Benefit text="Own the system immediately" />
                                    <Benefit text="Highest long-term cost savings" />
                                </Show>
                            </ul>
                        </div>

                        {/* ── Summary ── */}
                        <div class="mt-8 sm:mt-12 border border-brand-clay rounded-2xl py-6 px-4 sm:px-8 sm:py-8">
                            <h2 class="text-xl font-bold text-center sm:text-left">
                                Summary
                            </h2>

                            <div class="flex flex-col sm:flex-row justify-between items-center mt-6 gap-6">
                                <SummaryCard
                                    icon="/solar.svg"
                                    value={o().installation.size}
                                    unit="kWp"
                                    label="Installation Size"
                                />
                                <div class="hidden sm:block w-px h-24 bg-brand-clay" />
                                <div class="sm:hidden w-48 h-px bg-brand-clay" />
                                <SummaryCard
                                    icon="/energy_generated.svg"
                                    value={fmtNumber(
                                        o().installation.generated,
                                        loc()
                                    )}
                                    unit="MWh"
                                    label="Clean Energy / Year"
                                />
                                <div class="hidden sm:block w-px h-24 bg-brand-clay" />
                                <div class="sm:hidden w-48 h-px bg-brand-clay" />
                                <SummaryCard
                                    icon="/bill_savings.svg"
                                    value={`${fmtCurrency(o().savings.utilities.low, cur(), loc())} – ${fmtCurrency(o().savings.utilities.high, cur(), loc())}`}
                                    unit=""
                                    label="Annual Bill Savings"
                                />
                            </div>

                            {/* Details grid */}
                            <div class="grid grid-cols-2 sm:grid-cols-4 gap-y-3 mt-8 text-sm">
                                <span class="font-semibold">
                                    Est. roof size
                                </span>
                                <span class="sm:col-span-3">
                                    {fmtNumber(o().roof_size, loc())} m²
                                </span>
                                <span class="font-semibold">ROI</span>
                                <span class="sm:col-span-3">
                                    {o().installation.roi} years
                                </span>
                            </div>
                        </div>

                        {/* ── Savings ── */}
                        <div class="mt-6 sm:mt-12">
                            <h2 class="text-xl font-bold text-center sm:text-left">
                                Estimated Savings
                            </h2>
                            <div class="bg-brand-oat rounded-[20px] mt-5 px-5 xs:px-6 sm:px-10 py-5 sm:py-8">
                                <p class="font-semibold">
                                    Savings on electricity bills
                                </p>

                                <p class="text-xl font-bold mt-4">
                                    {tab() === 'PPA'
                                        ? `${fmtCurrency(o().savings.ppa.low, cur(), loc())} – ${fmtCurrency(o().savings.ppa.high, cur(), loc())}`
                                        : `${fmtCurrency(o().savings.utilities.low, cur(), loc())} – ${fmtCurrency(o().savings.utilities.high, cur(), loc())}`}
                                </p>
                                <p class="text-[#637381] text-sm sm:text-base">
                                    Annually
                                </p>

                                <div class="w-[120px] h-px bg-brand-clay my-4 sm:my-5" />

                                <p class="text-xl font-bold">
                                    {tab() === 'PPA'
                                        ? `${fmtCurrency(Number(o().savings.ppa.low) * 25, cur(), loc())} – ${fmtCurrency(Number(o().savings.ppa.high) * 25, cur(), loc())}`
                                        : `${fmtCurrency(Number(o().savings.utilities.low) * 25, cur(), loc())} – ${fmtCurrency(Number(o().savings.utilities.high) * 25, cur(), loc())}`}
                                </p>
                                <p class="text-[#637381] text-sm sm:text-base">
                                    Over 25 years
                                </p>

                                <p class="italic text-xs leading-relaxed mt-4 text-[#637381]">
                                    These savings are indicative and may vary
                                    based on actual energy consumption patterns,
                                    solar panel efficiency, and changes in
                                    electricity tariffs.
                                </p>
                            </div>
                        </div>

                        {/* ── Environmental Impact ── */}
                        <div class="mt-6 sm:mt-10">
                            <h2 class="text-xl font-bold text-center sm:text-left">
                                Your environmental impact
                            </h2>
                            <div class="border border-[#D2D2D2] rounded-[20px] my-5 py-6 flex flex-col xs:flex-row items-center justify-center gap-6 xs:gap-4">
                                <ImpactCard
                                    icon="/car-black.svg"
                                    value={o().impact.cars}
                                    label="cars off the road"
                                />
                                <ImpactCard
                                    icon="/tree-black.svg"
                                    value={o().impact.trees}
                                    label="trees planted"
                                />
                                <ImpactCard
                                    icon="/co2-black.svg"
                                    value={o().impact.co2}
                                    label="tons CO₂ / year"
                                />
                            </div>
                        </div>

                        {/* ── Actions ── */}
                        <div class="mt-10 flex gap-3">
                            <button
                                type="button"
                                onClick={handleShare}
                                class="px-5 py-3.5 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                                {copied() ? 'Copied!' : 'Share'}
                            </button>
                            <button
                                type="button"
                                onClick={() =>
                                    navigate({
                                        to: '/$locale',
                                        search: (prev) => prev
                                    })
                                }
                                class="flex-1 py-3.5 rounded-full bg-brand-yellow text-brand-black font-bold text-base hover:opacity-90 transition-opacity">
                                Start a new calculation
                            </button>
                        </div>
                    </div>
                )}
            </Show>

            {/* WhatsApp floating button */}
            <a
                href={rootContext().config.whatsappLink || '#'}
                target="_blank"
                rel="noopener noreferrer"
                class="fixed bottom-8 right-8 w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg hover:scale-105 transition-transform">
                <img
                    src="/whatsapp.svg"
                    alt="WhatsApp"
                    width={24}
                    height={24}
                />
            </a>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Benefit(props: { text: string }) {
    return (
        <li class="flex items-start gap-3">
            <img
                src="/tick.svg"
                alt=""
                width={24}
                height={24}
                class="shrink-0 mt-0.5"
            />
            <span class="text-sm sm:text-base">{props.text}</span>
        </li>
    );
}

function SummaryCard(props: {
    icon: string;
    value: string | number;
    unit: string;
    label: string;
}) {
    return (
        <div class="flex sm:flex-col items-center sm:text-center w-[248px] xs:w-[318px] sm:w-[184px]">
            <img src={props.icon} alt="" class="w-8 sm:w-12 h-6 sm:h-10" />
            <div class="ml-4 sm:ml-0 sm:mt-4">
                <p class="text-xl sm:text-2xl font-bold">
                    {props.value}{' '}
                    <span class="text-xs sm:text-base">{props.unit}</span>
                </p>
            </div>
            <p class="ml-auto sm:ml-0 text-sm sm:text-base sm:mt-2 text-[#637381]">
                {props.label}
            </p>
        </div>
    );
}

function ImpactCard(props: {
    icon: string;
    value: string | number;
    label: string;
}) {
    return (
        <div class="flex flex-col items-center w-[100px] sm:w-[148px]">
            <img src={props.icon} alt="" width={40} height={40} />
            <p class="font-bold mt-2 text-sm sm:text-base">
                {fmtNumber(props.value, locale().localeString)}
            </p>
            <p class="text-[#637381] text-sm sm:text-base text-center">
                {props.label}
            </p>
        </div>
    );
}
